/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {React} from '@api'
import { Utils, HTMLContainer, TLBounds } from '@tldraw/core'
import {StickyShape, TDMeta, TransformInfo, getBoundsRectangle, TextAreaUtils, getStickyFontStyle, normalizeText, createDefaultTextStyle, getTextSvgElement} from '@modules/whiteboard'
import {TDShapeUtil} from '@modules/whiteboard/shapes/TDShapeUtil'
import Vec from '@tldraw/vec'
import {useObserver} from 'mobx-react-lite'
import * as css from './StickyUtil.css'
import {transformSingleRectangle, transformRectangle} from '../shared'

type T = StickyShape
type E = HTMLDivElement

export class StickyUtil extends TDShapeUtil<T, E> {
  type = 'sticky' as const

  canBind = true

  canEdit = true

  //hideResizeHandles = false

  getShape = (props: Partial<T>): T => {
    return Utils.deepMerge<T>(
      {
        id: 'id',
        type: 'sticky',
        name: 'Sticky',
        parentId: 'page',
        childIndex: 1,
        point: [0, 0],
        size: [200, 200],
        text: '',
        rotation: 0,
        style: createDefaultTextStyle(),
      },
      props
    )
  }

  Component = TDShapeUtil.Component<T, E, TDMeta>(
    ({ shape, meta, events, isGhost, isEditing, onShapeBlur, onShapeChange }, ref) => {
      return useObserver(() => {
        const font = getStickyFontStyle(shape.style),
          {style: {textColor, backgroundColor}} = shape

        const rContainer = React.useRef<HTMLDivElement>(null)

        const rTextArea = React.useRef<HTMLTextAreaElement>(null)

        const rText = React.useRef<HTMLDivElement>(null)

        const rIsMounted = React.useRef(false)

        const handlePointerDown = React.useCallback((e: React.PointerEvent) => {
          e.stopPropagation()
        }, [])

        const handleTextChange = React.useCallback(
          (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onShapeChange?.({
              id: shape.id,
              type: shape.type,
              text: normalizeText(e.currentTarget.value),
            })
          },
          [onShapeChange]
        )

        const handleKeyDown = React.useCallback(
          (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Escape') return

            if (e.key === 'Tab' && shape.text.length === 0) {
              e.preventDefault()
              return
            }

            e.stopPropagation()

            if (e.key === 'Tab') {
              e.preventDefault()
              if (e.shiftKey) {
                TextAreaUtils.unindent(e.currentTarget)
              } else {
                TextAreaUtils.indent(e.currentTarget)
              }

              onShapeChange?.({...shape, text: normalizeText(e.currentTarget.value)})
            }
          },
          [shape, onShapeChange]
        )

        const handleBlur = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
          e.currentTarget.setSelectionRange(0, 0)
          onShapeBlur?.()
        }, [])

        const handleFocus = React.useCallback(
          (e: React.FocusEvent<HTMLTextAreaElement>) => {
            if (!isEditing) return
            if (!rIsMounted.current) return
            e.currentTarget.select()
          },
          [isEditing]
        )

        // Focus when editing changes to true
        React.useEffect(() => {
          if (isEditing) {
            rIsMounted.current = true
            const elm = rTextArea.current!
            elm.focus()
            elm.select()
          }
        }, [isEditing])

        // Resize to fit text
        React.useEffect(() => {
          const text = rText.current!

          const {size} = shape
          const {offsetHeight: currTextHeight} = text
          const minTextHeight = MIN_CONTAINER_HEIGHT - PADDING * 2
          const prevTextHeight = size[1] - PADDING * 2

          // Same size? We can quit here
          if (currTextHeight === prevTextHeight) return

          if (currTextHeight > minTextHeight) {
            // Snap the size to the text content if the text only when the
            // text is larger than the minimum text height.
            onShapeChange?.({id: shape.id, size: [size[0], currTextHeight + PADDING * 2]})
            return
          }

          // if (currTextHeight < minTextHeight && size[1] > MIN_CONTAINER_HEIGHT) {
          //   // If we're smaller than the minimum height and the container
          //   // is too tall, snap it down to the minimum container height
          //   onShapeChange?.({id: shape.id, size: [size[0], MIN_CONTAINER_HEIGHT]})
          //   return
          // }

          const textarea = rTextArea.current
          textarea?.focus()
        }, [shape.text, shape.size[1], shape.style])

        const style = {
          font,
          textColor,
          fontSize: shape.style.fontSize,
          textShadow: meta.isDarkMode
                      ? `0.5px 0.5px 2px rgba(255, 255, 255,.25)`
                      : `0.5px 0.5px 2px rgba(255, 255, 255,.5)`,
        }

        return (
          <HTMLContainer ref={ref} {...events}>
            <div className={css.root}
                 //data-skew={shape.childIndex % 2 == 0 ? 'left' : 'right'}
                 ref={rContainer}
                 data-ghost={isGhost}
                 style={{backgroundColor, ...style}}
            >
              <div className={css.text} ref={rText} data-editing={isEditing} data-alignment={shape.style.textAlign}>
                {shape.text}&#8203;
              </div>
              {isEditing && (
                <textarea
                  className={css.textarea}
                  ref={rTextArea}
                  onPointerDown={handlePointerDown}
                  value={shape.text}
                  onChange={handleTextChange}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  tabIndex={-1}
                  autoComplete="false"
                  autoCapitalize="false"
                  autoCorrect="false"
                  autoSave="false"
                  autoFocus
                  spellCheck={false}
                  data-alignment={shape.style.textAlign}
                />
              )}
            </div>
          </HTMLContainer>
        )
      })
    }
  )

  Indicator = TDShapeUtil.Indicator<T>(({ shape }) => {
    const {
      size: [width, height],
    } = shape

    return (
      <rect x={0} y={0} rx={3} ry={3} width={Math.max(1, width)} height={Math.max(1, height)} />
    )
  })

  getBounds = (shape: T) => {
    return getBoundsRectangle(shape)
  }

  shouldRender = (prev: T, next: T) => {
    return next.size !== prev.size || next.style !== prev.style || next.text !== prev.text
  }

  transform = (
    shape: T,
    bounds: TLBounds,
    { scaleX, scaleY, transformOrigin }: TransformInfo<T>
  ): Partial<T> => {
    const point = Vec.toFixed([
      bounds.minX +
        (bounds.width - shape.size[0]) * (scaleX < 0 ? 1 - transformOrigin[0] : transformOrigin[0]),
      bounds.minY +
        (bounds.height - shape.size[1]) *
          (scaleY < 0 ? 1 - transformOrigin[1] : transformOrigin[1]),
    ])

    return {
      point,
    }
  }

  transformSingle = transformSingleRectangle

  getSvgElement = (shape: T): SVGElement | void => {
    const bounds = this.getBounds(shape),
      textElm = getTextSvgElement(shape, bounds),
      {style: {textColor, backgroundColor}} = shape

    textElm.setAttribute('fill', textColor)

    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    rect.setAttribute('width', bounds.width + '')
    rect.setAttribute('height', bounds.height + '')
    rect.setAttribute('fill', backgroundColor)

    g.appendChild(rect)
    g.appendChild(textElm)

    return g
  }
}

/* -------------------------------------------------- */
/*                       Helpers                      */
/* -------------------------------------------------- */

const PADDING = 16
const MIN_CONTAINER_HEIGHT = 200


// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/sticky/StickyUtil.tsx